import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { X, AlertTriangle } from 'lucide-react'
import type { PricingItem } from '@/types'

interface DeleteItemDialogProps {
  /** Item awaiting delete confirmation. Ignored while `blockedMessage` is set. */
  item?: PricingItem
  /** Server's refusal text. When set, the dialog explains instead of offering to delete. */
  blockedMessage?: string
  deleting?: boolean
  onConfirm: () => void
  onClose: () => void
}

/**
 * Two states, one dialog:
 *
 *  - Confirm — the item may be deletable, and deletion is permanent.
 *  - Blocked — the server refused (formula-linked, or referenced by line items).
 *
 * The confirm step runs BEFORE the request, because the client doesn't know an
 * item's reference count. A referenced item therefore costs one confirm click
 * before the refusal appears.
 */
export function DeleteItemDialog({
  item,
  blockedMessage,
  deleting,
  onConfirm,
  onClose,
}: DeleteItemDialogProps) {
  if (!item && !blockedMessage) return null

  const blocked = !!blockedMessage

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={deleting ? undefined : onClose} />

      <Card className="relative w-full max-w-md mx-4 z-50">
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              {blocked && <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />}
              <h2 className="text-lg font-semibold">
                {blocked ? "Can't delete this item" : `Delete ${item?.name}?`}
              </h2>
            </div>
            <button
              onClick={onClose}
              disabled={deleting}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-muted-foreground mb-6">
            {blocked
              ? blockedMessage
              : 'This permanently removes the item from your pricing catalog. It cannot be undone.'}
          </p>

          <div className="flex gap-3">
            {blocked ? (
              <Button type="button" onClick={onClose} className="flex-1">
                Close
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={deleting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onConfirm}
                  disabled={deleting}
                  className="flex-1"
                >
                  {deleting ? 'Deleting…' : 'Delete'}
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
